import type { PermissionKey, Role, ScopeEntry } from '@nexa/auth';
import { http } from '@/lib/http';

/** The TanStack Query key every `GET /v1/me` reader shares (one fetch, one cache entry). */
export const ME_QUERY_KEY = ['auth', 'me'] as const;

/** A role the operator holds, as `GET /v1/me@v2` names it (system or this airline's custom). */
export interface IMeAccessRole {
  key: string;
  name: string;
  system: boolean;
}

/**
 * The `access` block of `GET /v1/me@v2` (operator-bff, users-roles-permissions R4).
 * Wire data: `lib/access` re-validates it and fails closed on anything malformed.
 */
export interface IMeAccess {
  roles: IMeAccessRole[];
  roleKeys: string[];
  permissions: PermissionKey[];
  scope: ScopeEntry[];
  /** Effective airports; `'*'` = the airline's whole operating set; `[]` = none. */
  airports: string[] | '*';
  /** Bumped on every access change; pushed by `WS access.changed`. */
  accessVersion: number;
  /** Signed in, but no role assigned — the console shows "Sin acceso asignado". */
  noAccess: boolean;
  /** `store` = an access-store assignment; `legacy` = derived from the token (M1). */
  source: 'store' | 'legacy' | (string & {});
}

export interface OperatorMe {
  operatorUrn: string;
  sub: string;
  airlineUrn: string;
  /** IATA-2 carrier code for this deployment (e.g. 'LA', 'H2'). Issue #600. */
  airlineIata: string;
  /**
   * The legacy role today's role-based guards evaluate. Since `/v1/me@v2` it is DERIVED
   * from the operator's roles (M1, brief §8.4) and `null` for a member with no access.
   */
  role: Role | null;
  orgId: string | null;
  displayName: string;
  /** `/v1/me@v2` only — absent on an older BFF, where access derives from `role`. */
  access?: IMeAccess;
}

export async function getMe(): Promise<OperatorMe> {
  return http.get('v1/me').json<OperatorMe>();
}
